import {
  Box,
  Button,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useMemo, useState } from "react";
import CellModal from "./CellsModal";
import { useQueryClient } from "react-query";
import TableComponent from "../../../CustomDataGrid";
import { Column } from "react-table";
import { Type } from "typescript";
import { FaCircle, FaPlus } from "react-icons/fa";
import { TbEyeEdit } from "react-icons/tb";
import { queryKeys } from "../../../../queries/keys";
import { useAllCells } from "../../../../queries/hooks";
import { CellDescDiv } from "./styles";
import { MdSearch } from "react-icons/md";

interface IProps {
  goTo(destination: string): void;
}

type CellActiveFilter = "all" | "true" | "false";

const Cells = ({ goTo }: IProps) => {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [cellActiveFilter, setCellActiveFilter] =
    useState<CellActiveFilter>("all");

  const { isLoading, data } = useAllCells();
  const [cellEdit, setCellEdit] = useState<any>();
  const [showCellModal, setShowCellModal] = useState(false);

  const filteredCells = useMemo(() => {
    if (!data || !Array.isArray(data)) return [];
    const q = search.trim().toLowerCase();

    return data.filter((cell: any) => {
      const name = String(cell.name ?? "").toLowerCase();
      if (q && !name.includes(q)) return false;

      if (cellActiveFilter === "true" && cell.active !== true) return false;
      if (cellActiveFilter === "false" && cell.active !== false) return false;

      return true;
    });
  }, [data, search, cellActiveFilter]);

  const hasCellsFromApi = (data?.length ?? 0) > 0;
  const paginationResetKey = `${search.trim()}-${cellActiveFilter}`;
  const emptyStateMessage =
    hasCellsFromApi && filteredCells.length === 0
      ? "Nenhum resultado encontrado para os filtros aplicados."
      : undefined;

  interface ICellRow extends Type {
    _id: string;
    name: string;
    leader_user_ids: string;
    active: boolean;
    projects: string;
    projectCount: number;
  }
  const columns2: Column<ICellRow>[] = [
    {
      Header: "NOME DA CÉLULA",
      accessor: "name",
      Filter: "",
      Cell: ({ cell }) => {
        return (
          <CellDescDiv style={{}}>
            <div>
              <span>
                {cell.row.original.active ? (
                  <FaCircle size={10} color="green" />
                ) : (
                  <FaCircle size={10} color="red" />
                )}{" "}
              </span>
            </div>
            <div>
              <span>{cell.row.values.name}</span>
            </div>
          </CellDescDiv>
        );
      },
    },
    {
      Header: "USUÁRIOS",
      accessor: "leader_user_ids",
      Filter: "",
      Cell: ({ cell }) => (
        <span> {`${cell.value.length} ativos; 0 inativos`}</span>
      ),
    },
    {
      Header: "PROJETOS (#MM'S)",
      accessor: "projects",
      Cell: ({ cell }) => (
        <span>{cell.row.original.projectCount ?? 0} ativos</span>
      ),
      Filter: "",
    },
    {
      Header: "Ações",
      accessor: "_id",
      Cell: ({ cell }) => (
        <div
          onClick={() => handleCellEdit(cell.value)}
          style={{ cursor: "pointer" }}
        >
          <TbEyeEdit
            size={18}
            style={{
              cursor: "pointer",
              border: "1px solid #1976D2",
              padding: 2,
              borderRadius: 4,
            }}
            color="#1976D2"
          />
        </div>
      ),
      Filter: "",
      disableSortBy: true,
      width: 30,
    },
  ];

  function handleCellEdit(id: string) {
    let selectedCell = data?.find((cell: { _id: string }) => cell._id === id);
    if (!!selectedCell) {
      setCellEdit(selectedCell);
      setShowCellModal(true);
    }
  }

  return (
    <Box px={"24px"} pt={"24px"} pb={"28px"} sx={{ flex: 1 }}>
      {(showCellModal || cellEdit) && (
        <CellModal
          cell={cellEdit}
          onGoTo={goTo}
          onClose={() => {
            setShowCellModal(false);
            setCellEdit(undefined);
          }}
          onSuccess={() => queryClient.invalidateQueries(queryKeys.cellsAll)}
        />
      )}

      <div
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 20 }}>Células</span>
        <Button
          variant="contained"
          startIcon={<FaPlus size={12} />}
          onClick={() => setShowCellModal(true)}
        >
          Adicionar célula
        </Button>
      </div>

      <Box display={"flex"} mb={4} alignItems={"center"} flex={1} gap={2}>
        <TextField
          size="small"
          placeholder="Pesquisar por nome da célula"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <MdSearch />
              </InputAdornment>
            ),
          }}
          sx={{ width: "80%" }}
        />
        <Box display={"flex"} alignItems={"center"} flex={1} gap={2}>
          <FormControl sx={{ width: "100%" }}>
            <InputLabel id="cells-filter-status" size="small">
              Status
            </InputLabel>
            <Select
              labelId="cells-filter-status"
              label="Status"
              size="small"
              value={cellActiveFilter}
              onChange={(e) =>
                setCellActiveFilter(e.target.value as CellActiveFilter)
              }
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="true">Ativas</MenuItem>
              <MenuItem value="false">Inativas</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ minHeight: 400 }}>
        <TableComponent
          columns={columns2}
          isLoading={isLoading}
          data={filteredCells}
          paginationResetKey={paginationResetKey}
          emptyStateMessage={emptyStateMessage}
        />
      </Box>
    </Box>
  );
};

export default Cells;
